// Per-questionnaire progress for the admin vendor detail page. Each
// questionnaire tab shows its own Progress bar with the SAME numbers the
// vendor's post-submit view shows ("All questions (N)" / "N complete"):
// every visible question counts toward the total, and a question is complete
// exactly when the vendor owes no action on it. The per-question decision is
// delegated to needsVendorAction — the single source of truth shared with the
// vendor nav badge, dashboard, and Needs-action tab — so the two portals can
// never drift. Kept pure (no React / no network) so it can be unit-tested
// directly, the same discipline as companyDetailTabs.ts and wizard.ts.

import { needsVendorAction } from '@l2u/ui/lib/vendorActions.ts';
import { isAwaitingAdmin, type ReviewSubmission } from '@l2u/ui/lib/reviewState.ts';
import type { CompletionReason } from '@l2u/ui/lib/visibility.ts';
import type { Question, Submission } from '@l2u/ui/lib/types.ts';
import type { QuestionnaireGroup } from './companyDetailTabs.ts';

export type QuestionnaireProgress = {
  /** Visible questions in this questionnaire, required or not. */
  total: number;
  /** Of those, how many need no further vendor action. */
  completed: number;
  /** Rounded percentage; 0 when there are no visible questions (matches the vendor bar). */
  percent: number;
};

/**
 * Count completed vs total visible questions for one questionnaire tab.
 *
 * Only the questionnaire's visible (per `visibleIds`) questions are counted —
 * the same population the vendor's post-submit list renders, including
 * optional, compliance-exempt and retired questions. Completion mirrors the
 * vendor exactly: sent-back (rejected) or admin-commented-last items and
 * unanswered required questions are outstanding; everything else (answered,
 * approved / N-A, or simply not required) is complete.
 */
export function questionnaireProgress(
  group: QuestionnaireGroup,
  visibleIds: Set<string>,
  subByQ: ReadonlyMap<string, ReviewSubmission>,
  completionByQuestion: Map<string, CompletionReason | null>,
): QuestionnaireProgress {
  let total = 0;
  let completed = 0;
  for (const section of group.sections) {
    for (const question of section.questions) {
      if (!visibleIds.has(question.id)) continue;
      total += 1;
      if (needsVendorAction(question, subByQ.get(question.id), completionByQuestion) === null) {
        completed += 1;
      }
    }
  }
  const percent = total === 0 ? 0 : Math.round((completed * 100) / total);
  return { total, completed, percent };
}

export type ReviewProgress = {
  /** Answered questions the admin has acted on (approved / N-A / sent back / commented last). */
  reviewed: number;
  /** Answered questions still awaiting an admin decision. */
  needReview: number;
  /** Rounded percentage of reviewable items already reviewed; 0 when nothing is reviewable. */
  percent: number;
};

/**
 * The admin's own review progress for one questionnaire tab ("N reviewed ·
 * M need review"). Only visible questions WITH a submission count — an
 * unanswered question has nothing to review yet. A question needs review
 * exactly when isAwaitingAdmin says the admin owes the next move, so this bar,
 * the "Action required" pill and the outstanding badges all agree.
 */
export function reviewProgress(
  group: QuestionnaireGroup,
  visibleIds: Set<string>,
  subByQ: ReadonlyMap<string, ReviewSubmission>,
): ReviewProgress {
  let reviewed = 0;
  let needReview = 0;
  for (const section of group.sections) {
    for (const question of section.questions) {
      if (!visibleIds.has(question.id)) continue;
      // Task 8: a question marked "not part of compliance" needs no admin
      // decision at all, so it is not reviewable work and must not dilute this
      // bar. (questionnaireProgress deliberately still counts it — that bar
      // mirrors the vendor's own "All questions" list.)
      if (question.exclude_from_compliance) continue;
      const sub = subByQ.get(question.id);
      if (!sub) continue;
      if (isAwaitingAdmin(sub)) needReview += 1;
      else reviewed += 1;
    }
  }
  const reviewable = reviewed + needReview;
  const percent = reviewable === 0 ? 0 : Math.round((reviewed * 100) / reviewable);
  return { reviewed, needReview, percent };
}

/**
 * Task 14: an optional question the vendor never answered is not review work.
 * The admin sees no "Awaiting document" placeholder and no verdict controls for
 * it — there is nothing to decide until an answer exists.
 *
 * "Unanswered" is delegated to the shared completion engine, so it means exactly
 * what the progress bar means by it rather than a second, drifting definition.
 *
 * Presentation only: reviewProgress already skips rows with no submission, so
 * hiding the controls cannot double-subtract from the review percentage.
 */
export function optionalUnanswered(
  question: Question,
  submission: Submission | undefined,
): boolean {
  if (question.required) return false;
  if (!submission) return true;
  // Emptiness of the submission, not the completion reason. Those are not the
  // same test: a document_upload question with no file and a text question
  // answered "Yes" with the file still missing BOTH report 'upload_required',
  // but only the first is genuinely unanswered. Keying off the reason (or off
  // `!== null`, as this did) hides the verdict controls on the second while
  // AnswerPreview renders the vendor's answer directly above them — and the
  // vendor is meanwhile told the row needs action by docStats.
  const answer = submission.answer;
  const hasAnswer =
    answer !== null &&
    answer !== undefined &&
    !(typeof answer === 'string' && answer.trim() === '') &&
    !(Array.isArray(answer) && answer.length === 0);
  return (
    !hasAnswer &&
    submission.submission_files.length === 0 &&
    submission.submission_adoptions.length === 0
  );
}
